import {
  ChakraFlex,
  ChakraVStack,
  Tag,
  TagColor,
  Text,
} from '@equip.health/ui';
import SelectableUserAvatarBlock, {
  AvatarUser,
} from '../SelectableUserAvatarBlock';
import {
  PROVIDER_LIST_TEST_ID,
  PROPOSED_BADGE_TEXT,
  PROVIDER_TEAM_SECTION_LABEL,
} from '../constants';
import { UseGetCareTeamProvider } from '~/lib/hooks/care-team/useGetCareTeam';

export interface ProviderListProps {
  existingProviders: UseGetCareTeamProvider[];
  proposedProvider: GetProviderByIdResponse;
  proposedProviderRole: string;
}

const getCredentialsDisplay = (
  credentials?: string[] | { name?: string }[],
): string => {
  if (!credentials?.length) return '';
  if (typeof credentials[0] === 'string') {
    return (credentials as string[]).join(', ');
  }
  return (credentials as { name?: string }[])
    .map((c) => c.name)
    .filter(Boolean)
    .join(', ');
};

const ProviderList = ({
  existingProviders,
  proposedProvider,
  proposedProviderRole,
}: ProviderListProps) => {
  const proposedAvatarUser: AvatarUser = {
    userId: proposedProvider.userId,
    firstName: proposedProvider.firstName,
    lastName: proposedProvider.lastName,
    chosenName: proposedProvider.chosenName ?? undefined,
    pronouns: proposedProvider.pronouns,
    role: 'provider',
    providerTypeName: proposedProviderRole,
    avatar: proposedProvider.avatar,
    avatarThumbnailUrl: proposedProvider.avatar?.thumbnailUrl,
    avatarUrl: proposedProvider.avatar?.url,
    credentialsDetails: proposedProvider.credentialsDetails,
  };

  return (
    <ChakraFlex
      data-testid={PROVIDER_LIST_TEST_ID}
      flexDirection="column"
      gap="18px"
    >
      <Text variant="bodyMedium">{PROVIDER_TEAM_SECTION_LABEL}</Text>
      <ChakraVStack alignItems="stretch" spacing="12px">
        <ChakraFlex
          alignItems="center"
          borderColor="neutral.line"
          borderRadius="12px"
          borderWidth="1px"
          gap="12px"
          justifyContent="space-between"
          padding="6px 18px 6px 6px"
        >
          <ChakraFlex flex="1" minWidth={0} overflow="hidden">
            <SelectableUserAvatarBlock
              avatarSize="sm"
              id={`proposed-provider-${proposedProvider.userId}`}
              roleDisplay={[
                proposedProviderRole,
                getCredentialsDisplay(proposedProvider.credentialsDetails),
              ]
                .filter(Boolean)
                .join(', ')}
              showPrimaryTag={false}
              showPronunciation={false}
              showPronouns
              showTimezone={false}
              user={proposedAvatarUser}
              variant="compact"
            />
          </ChakraFlex>
          <ChakraFlex flexShrink={0}>
            <Tag
              color={TagColor.pink}
              id={`proposed-provider-badge-${proposedProvider.userId}`}
              value={PROPOSED_BADGE_TEXT}
            />
          </ChakraFlex>
        </ChakraFlex>

        {existingProviders
          .filter((provider) => provider.id !== proposedProvider.userId)
          .map((provider) => {
            const avatarUser: AvatarUser = {
              userId: provider.id,
              firstName: provider.firstName,
              lastName: provider.lastName,
              pronouns: provider.pronouns,
              role: 'provider',
              providerTypeName: provider.role,
              avatarUrl: provider.avatarSrc,
              credentialsDetails: provider.credentials?.map((c) =>
                typeof c === 'string' ? { name: c } : c,
              ),
            };

            const roleWithCredentials = [
              provider.role,
              getCredentialsDisplay(provider.credentials),
            ]
              .filter(Boolean)
              .join(', ');

            return (
              <ChakraFlex
                key={provider.id}
                alignItems="center"
                borderColor="neutral.line"
                borderRadius="12px"
                borderWidth="1px"
                gap="12px"
                justifyContent="space-between"
                padding="6px 18px 6px 6px"
              >
                <ChakraFlex flex="1" minWidth={0} overflow="hidden">
                  <SelectableUserAvatarBlock
                    avatarSize="sm"
                    id={`existing-provider-${provider.id}`}
                    roleDisplay={roleWithCredentials}
                    showPrimaryTag={false}
                    showPronunciation={false}
                    showPronouns
                    showTimezone={false}
                    user={avatarUser}
                    variant="compact"
                  />
                </ChakraFlex>
              </ChakraFlex>
            );
          })}
      </ChakraVStack>
    </ChakraFlex>
  );
};

export default ProviderList;
